import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import equal from 'fast-deep-equal';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { getIsPlanErknmStatusConsidered } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataErknmApproveApproved,
  getKnmDataNote,
  getKnmFormRef,
  getKnmIsChangePlanReasonActive,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsNew,
  getTargetEntitySelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { RafpRequestStatusTypeEnum } from 'APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { fnsCheckMessageByStatuses } from 'KnmErknmPackage/ErknmSubjectModule/constants/fnsCheckMessageByStatuses';
import {
  isFnsAddressFnsError,
  isOrganizationNameFnsError,
  isWorkAddressFnsError,
  renderFnsCheckMessage,
  renderFnsErvkEqualsError,
  valuesSubjectResultStatuses,
} from 'CorePackage/MainModule/common/renderFnsMessage';
import { FnsCheckStatusesEnum } from 'KnmErknmPackage/ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { checkOgrnBySubjectType } from 'CorePackage/MainModule/validators/isOgrn';
import { ICountry } from 'APIPackage/CatalogsModule/interfaces/ICountry';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { KnmSnilsField } from 'KnmPackage/MainModule/components/KnmSnilsField';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { MAX_CHARACTERS_ERROR, REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './ErknmSubjectInfo.module.scss';

export enum KnmSubjectTypesEnum {
  FIZ = 'Физические лица',
  UR = 'Юридические лица',
  IP = 'Индивидуальные предприниматели',
  IFIZ = 'Иностранные физические лица',
  IUR = 'Иностранные юридические лица',
  FIUR = 'Филиалы иностранных юридических лиц',
  OFFICIAL = 'Должностные лица',
}

interface IErknmSubjectInfoProps {
  ervkId: string | undefined;
  checkSubjectResult: IKnmCheckSubjectResult | null;
  inn: string | undefined;
  innLabelBlockId: string;
  inspectionResults?: IInspectionResult[];
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  isExtended: boolean;
  isNewOrganizationTemplate?: boolean;
  isWatchOnly: boolean;
  kpp: string | undefined;
  kppLabelBlockId: string;
  lookupData?: ILegalEntityLookup | boolean | undefined;
  name: string | undefined;
  ogrn: string | undefined;
  ogrnLabelBlockId: string;
  organizationNameBlockId: string;
  addressBlockId: string;
  rafpLabelBlockId: string;
  rafpNza: string | undefined;
  setLookupData?: Dispatch<SetStateAction<ILegalEntityLookup | boolean | undefined>>;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectResult: IKnmCheckSubjectResult | null;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  listId?: string;
}

interface IErknmSubjectInfoStoreProps {
  formRef: IKnmFormRef | undefined;
  isChangePlanReasonActive: boolean;
  isEditableInStatusCommentsExist: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isNewKnm: boolean;
  isSupervisor: boolean | undefined;
  knmDataApprovedType: ApprovedTypesEnum | null | undefined;
  knmDataNote: string | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const ErknmSubjectInfoWithoutStore = ({
  ervkId,
  checkSubjectResult,
  inn,
  innLabelBlockId,
  inspectionResults,
  isAutocomplete,
  isExtended,
  isNewOrganizationTemplate,
  isWatchOnly,
  kpp,
  kppLabelBlockId,
  lookupData,
  name,
  ogrn,
  ogrnLabelBlockId,
  organizationNameBlockId,
  rafpLabelBlockId,
  addressBlockId,
  rafpNza,
  setLookupData,
  smevRequestStatus,
  snils,
  subjectResult,
  subjectType,
  formRef,
  isChangePlanReasonActive,
  isEditableInStatusCommentsExist,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isNewKnm,
  isSupervisor,
  knmDataApprovedType,
  knmDataNote,
  knmPlanStatus,
  subjectTypeOptions,
  listId,
}: IErknmSubjectInfoProps & IErknmSubjectInfoStoreProps) => {
  const isKnmPlanStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanStatus);

  const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
  const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;
  const isUR = subjectType?.label === KnmSubjectTypesEnum.UR;
  const isIP = subjectType?.label === KnmSubjectTypesEnum.IP;
  const isFIUR = subjectType?.label === KnmSubjectTypesEnum.FIUR;

  const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();
  const fnsCheckMessage = fnsCheckMessageByStatuses.find(item =>
    equal(item.statuses.sort(), valuesSubjectResultStatuses(subjectResult)?.sort()),
  );

  const isFizFieldName = `${name}.isFiz`;
  const ogrnFieldName = `${name}.ogrn`;
  const innFieldName = `${name}.inn`;
  const organizationNameFieldName = `${name}.organizationName`;
  const subjectTypeFieldName = `${name}.subjectType`;
  const kppFieldName = `${name}.kpp`;
  const rafpNzaFieldName = `${name}.rafpNza`;
  const isForeignSubjectAgencyNotRegistredInRfFieldName = `${name}.isForeignSubjectAgencyNotRegistredInRf`;
  const countryFieldName = `${name}.country`;
  const locationAddressFieldName = `${name}.locationAddress`;
  const workAddressFieldName = `${name}.workAddress`;
  const factualAddressFieldName = `${name}.factualAddress`;
  const snilsFieldName = `${name}.snils`;
  const documentFieldName = `${name}.document`;

  const rafpNotFoundMessage =
    'Запрашиваемые сведения отсутствуют в РАФП, проверьте введенные значения. Если у данного иностранного ' +
    'юридического лица нет филиалов и представительств, зарегистрированных на территории РФ, ' +
    'то необходимо выбрать другой тип контролируемого лица – Иностранные юридические лица';

  const [rafpNzaNotFoundText, setRafpNzaNotFoundText] = useState<string | undefined>(rafpNotFoundMessage);
  const [isFnsCheckInProgress, setIsFnsCheckInProgress] = useState(
    fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.IN_PROGRESS,
  );
  const [isFnsCheckFound, setIsFnsCheckFound] = useState(
    fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.FOUND,
  );

  const handleLegalEntityAutoCompleteSelect = useCallback(
    (legalEntityLookup: ILegalEntityLookup) => {
      if (subjectType?.label !== KnmSubjectTypesEnum.IUR) {
        const {
          ogrn: legalEntityOgrn,
          inn: legalEntityInn,
          orgName: legalEntityOrgName,
          dateReg: legalEntityDateReg,
          status: legalEntityStatus,
          leAddress: legalEntityLeAddress,
          workAddress: legalEntityWorkAddress,
          mspCategory: legalMspCategory,
          mspRegistryDate: legalMspRegistryDate,
        } = legalEntityLookup;

        formRef?.current.change(`${name}.organizationAutocomplete` as keyof IKnmForm, true);
        if (legalEntityOgrn) {
          formRef?.current.change(ogrnFieldName as keyof IKnmForm, legalEntityOgrn);
        }
        if (legalEntityInn) {
          formRef?.current.change(innFieldName as keyof IKnmForm, legalEntityInn);
        }
        if (legalEntityInn && legalEntityOgrn && checkOgrnBySubjectType(subjectType?.label, legalEntityOgrn)) {
          formRef?.current.change(`${name}.isAutocomplete` as keyof IKnmForm, true);
        }
        if (legalEntityOrgName) {
          formRef?.current.change(organizationNameFieldName as keyof IKnmForm, legalEntityOrgName);
        }
        if (legalEntityWorkAddress && isUR) {
          formRef?.current.change(workAddressFieldName as keyof IKnmForm, legalEntityWorkAddress);
        }
        if (legalEntityLeAddress && isUR) {
          formRef?.current.change(locationAddressFieldName as keyof IKnmForm, legalEntityLeAddress);
        }
        if (legalEntityLeAddress && isIP) {
          formRef?.current.change(factualAddressFieldName as keyof IKnmForm, legalEntityLeAddress);
        }
        if (legalEntityDateReg) {
          formRef?.current.change(
            `${name}.registrationDate` as keyof IKnmForm,
            formatDate({ date: legalEntityDateReg, dateFormat: DATE_FORMAT_LONG_FNS }),
          );
        }
        if (legalEntityStatus) {
          formRef?.current.change(`${name}.organizationStatus` as keyof IKnmForm, legalEntityStatus);
        }
        if (legalMspCategory) {
          formRef?.current.change(
            `${name}.mspCode` as keyof IKnmForm,
            mspCodesByMspCategoryDigitCodes[Number(legalMspCategory) as MspCategoryDigitCodesEnum],
          );
        }
        if (legalMspRegistryDate) {
          formRef?.current.change(`${name}.mspRegistryDate` as keyof IKnmForm, legalMspRegistryDate);
        }
      }
    },
    [formRef, subjectType, innFieldName, name, ogrnFieldName, organizationNameFieldName, isUR, isIP],
  );

  useEffect(() => {
    if (inn && ogrn) {
      dispatchKnmSetLegalEntityData({
        inn,
        ogrn,
      });
    }
  }, [dispatchKnmSetLegalEntityData, inn, ogrn]);

  const handleAutoCompleteClear = useCallback(() => {
    formRef?.current.change(ogrnFieldName as keyof IKnmForm);
    formRef?.current.change(innFieldName as keyof IKnmForm);
    formRef?.current.change(organizationNameFieldName as keyof IKnmForm);
    formRef?.current.change(workAddressFieldName as keyof IKnmForm);
    formRef?.current.change(locationAddressFieldName as keyof IKnmForm);
    dispatchKnmSetLegalEntityData(undefined);
    formRef?.current.change(`${name}.isAutocomplete` as keyof IKnmForm, false);
    formRef?.current.change(`${name}.mspCode` as keyof IKnmForm);
    clearItAccreditation();
    if (isExtended) {
      const objects = formRef?.current.getFieldState('objectsErknm')?.value;
      const autocompleteObjectIndex = objects?.findIndex(object => object.autocomplete);

      if (typeof autocompleteObjectIndex === 'number' && autocompleteObjectIndex >= 0) {
        const autocompleteFieldName = `objectsErknm'[${autocompleteObjectIndex}].autocomplete` as keyof IKnmForm;

        formRef?.current.change(autocompleteFieldName, false);
      }
    }
  }, [
    dispatchKnmSetLegalEntityData,
    formRef,
    innFieldName,
    isExtended,
    name,
    ogrnFieldName,
    organizationNameFieldName,
  ]);

  const handleRafpNzaChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      // Сбросить ИНН и КПП
      if (v.target.value) {
        formRef?.current.change(innFieldName as keyof IKnmForm, undefined);
        formRef?.current.change(kppFieldName as keyof IKnmForm, undefined);
        if (v.target.value !== rafpNza) {
          setRafpNzaNotFoundText(undefined);
        } else if (isRafpNzaNotFound) {
          setRafpNzaNotFoundText(rafpNotFoundMessage);
        }
      }
    },
    [formRef, innFieldName, kppFieldName],
  );

  const handleKppChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      if (v.target.value) {
        // Сбросить РАФП
        formRef?.current.change(rafpNzaFieldName as keyof IKnmForm, undefined);
      }
    },
    [formRef, rafpNzaFieldName],
  );

  useEffect(() => {
    if (!isAutocomplete) {
      if (isFIZ) {
        formRef?.current.change(ogrnFieldName as keyof IKnmForm);
        if (isExtended) {
          formRef?.current.change(`${name}.okveds` as keyof IKnmForm);
        }
      }
    }
  }, [
    formRef,
    isAutocomplete,
    isExtended,
    isFIZ,
    name,
    ogrnFieldName,
    organizationNameFieldName,
    subjectTypeFieldName,
  ]);

  useEffect(() => {
    setIsFnsCheckInProgress(fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.IN_PROGRESS);
    setIsFnsCheckFound(fnsCheckMessage && fnsCheckMessage?.type === FnsCheckStatusesEnum.FOUND);
  }, [fnsCheckMessage]);

  const isFieldEnabled =
    (!isWatchOnly &&
      !isFnsCheckInProgress &&
      isSupervisor &&
      (isNewOrganizationTemplate || isNewKnm || isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
      !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote)) ||
    ((isChangePlanReasonActive || isEditableInStatusCommentsExist) && !ervkId);

  const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
  const isRafpNzaNotFound = smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
  const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
  const isInnFieldEnabled = isFieldEnabled || isRafpNzaNotFound;

  const isOrganizationNameFieldEnabledWithoutFiz =
    !isWatchOnly &&
    isSupervisor &&
    (isNewOrganizationTemplate || isNewKnm || isKnmStatusFillInProcess || (isKnmStatusReadyForApprove && isFIZ)) &&
    !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote);

  const isOrganizationNameFieldEnabled = isFIZ && isOrganizationNameFieldEnabledWithoutFiz;

  const getOrganizationNameFieldRequired = () => !(isOrganizationNameFieldEnabledWithoutFiz && isFIZ);

  const clearItAccreditation = useCallback(() => {
    formRef?.current.change(`${name}.isPresentInItRegistry` as keyof IKnmForm, undefined);
    formRef?.current.change(`${name}.itAccreditationResponse` as keyof IKnmForm, undefined);
  }, []);

  const handleSubjectTypeChange = useCallback(
    (v: IDictionarySelectOption<ITargetEntity>) => {
      formRef?.current.change(isFizFieldName as keyof IKnmForm, v?.label === KnmSubjectTypesEnum.FIZ);
      formRef?.current.change(innFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(ogrnFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(rafpNzaFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(kppFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(organizationNameFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(workAddressFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(locationAddressFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(factualAddressFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(snilsFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(documentFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.isAutocomplete` as keyof IKnmForm, false);
      formRef?.current.change(isForeignSubjectAgencyNotRegistredInRfFieldName as keyof IKnmForm, false);
      formRef?.current.change(countryFieldName as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.okveds` as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.mspCode` as keyof IKnmForm, undefined);
      formRef?.current.change(`${name}.smevRequestStatus` as keyof IKnmForm, undefined);

      clearItAccreditation();
    },
    [
      formRef,
      name,
      isFizFieldName,
      innFieldName,
      ogrnFieldName,
      rafpNzaFieldName,
      kppFieldName,
      organizationNameFieldName,
      isForeignSubjectAgencyNotRegistredInRfFieldName,
      countryFieldName,
    ],
  );

  const normalizeFio = (value: string | undefined) => {
    if (!value) return value;

    return value.replace(/[^а-яёa-z-`' ]/gi, '');
  };

  const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);

  const saveKnmAndWaitFromRafpWarning =
    (isRafpFilled || (inn && kpp)) && !smevRequestStatus
      ? 'Сохраните КНМ и ожидайте загрузки информации из РАФП'
      : undefined;

  const waitFromRafpWarning = isRafpNzaSent ? 'Ожидайте загрузки информации из РАФП' : undefined;

  const rafpWarning = isFIUR ? saveKnmAndWaitFromRafpWarning || waitFromRafpWarning : undefined;

  const innOrSnilsWarning = isFIZ && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
  const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
  const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

  const IROrganizationName = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NAME,
  );
  const IRLocationAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_ADDRESS,
  );
  const IRFactualAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_FACTUAL_ADDRESS,
  );
  const IRRafpNza = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.RAFP_REGISTRY);

  return (
    <>
      <Row>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: `Тип контролируемого лица`,
            field: subjectTypeFieldName,
            isKnm: true,
          })}
          placementLabel="left"
          id={`acts[${listId}].subjectType`}
          color="black"
          className={styles.FieldBlock}
          tooltip={
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующем поле:
                  <ul>
                    <p>Контрольный (надзорный) орган</p>
                    <p>Вид контроля (надзора)</p>
                    <p>Основания проведения КНМ</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          }
        >
          <SelectInputField
            name={subjectTypeFieldName}
            options={subjectTypeOptions}
            disabled={!isFieldEnabled}
            size="large"
            onChange={handleSubjectTypeChange}
          />
        </FieldBlock>
      </Row>

      {subjectType && (
        <>
          {((subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ) ||
            subjectType.label === KnmSubjectTypesEnum.FIUR ||
            isFIZ) && (
            <Row>
              {subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Идентификационный номер налогоплательщика (ИНН)`,
                    field: innFieldName,
                    isKnm: true,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={innLabelBlockId}
                >
                  <KnmInnField
                    name={innFieldName}
                    clearable={isInnFieldEnabled && isAutocomplete}
                    disabled={!isInnFieldEnabled || isAutocomplete || !!rafpNza || isRafpNzaSent}
                    inn={inn}
                    isFnsCheckFound={isFnsCheckFound}
                    isFiz={isFIZ}
                    setLookupData={setLookupData}
                    isShowAutoCompleteField={
                      subjectType.label !== KnmSubjectTypesEnum.IUR && subjectType.label !== KnmSubjectTypesEnum.FIUR
                    }
                    onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                    onClear={handleAutoCompleteClear}
                    onLoadingChange={setFnsLookupByInnLoadingFlag}
                    warning={
                      innOrSnilsWarning ||
                      rafpWarning ||
                      (!isFIZ && !smevRequestStatus && renderFnsCheckMessage(subjectResult, lookupData, isAutocomplete))
                    }
                    onChangeInn={clearItAccreditation}
                    ervkId={ervkId}
                    subjectType={subjectType}
                    checkSubjectResult={checkSubjectResult}
                    isShowAdditionalMessages={true}
                  />
                  {isRafpNzaNotFound && inn && kpp && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
                </FieldBlock>
              )}
              {subjectType.label === KnmSubjectTypesEnum.FIUR && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Номер записи об аккредитации в РАФП`,
                    field: rafpNzaFieldName,
                    isKnm: true,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={rafpLabelBlockId}
                >
                  <div className={styles.ColumnWrapper}>
                    <TextInputField
                      name={rafpNzaFieldName}
                      disabled={!!inn || !!kpp || (!isRafpNzaNotFound && isRafpNzaSent)}
                      onChange={handleRafpNzaChange}
                      warning={rafpWarning}
                      inspectionResult={IRRafpNza}
                    />
                    {isRafpNzaNotFound && !inn && !kpp && (
                      <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                    )}
                    {isRafpNzaError && (
                      <Hint
                        text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос
                        будет произведен через час"
                      />
                    )}
                  </div>
                </FieldBlock>
              )}
              {isFIZ && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Страховой номер индивидуального лицевого счета (СНИЛС)`,
                    field: snilsFieldName,
                    isKnm: true,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={`acts[${listId}].snils`}
                >
                  <KnmSnilsField name={snilsFieldName} warning={innOrSnilsWarning} disabled={!isFieldEnabled} />
                </FieldBlock>
              )}
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.IP) && (
            <Row>
              <FieldBlock
                label="Основной государственный регистрационный номер (ОГРН)"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={ogrnLabelBlockId}
              >
                <KnmOgrnField
                  name={ogrnFieldName}
                  clearable={isFieldEnabled && isAutocomplete}
                  disabled={!isFieldEnabled || isAutocomplete}
                  ogrn={ogrn}
                  onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                  onClear={handleAutoCompleteClear}
                  onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.FIUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Код причины постановки на учет (КПП)`,
                  field: kppFieldName,
                  isKnm: true,
                })}
                id={kppLabelBlockId}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  name={kppFieldName}
                  disabled={isWatchOnly || !!rafpNza || isRafpNzaSent}
                  onChange={handleKppChange}
                  warning={rafpWarning}
                />
                {isRafpNzaNotFound && (!!inn || !!kpp) && (
                  <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                )}
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR ||
            subjectType.label === KnmSubjectTypesEnum.IP ||
            subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Наименование проверяемого лица`,
                  field: organizationNameFieldName,
                  isKnm: true,
                })}
                id={organizationNameBlockId}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextareaField
                  id={organizationNameBlockId}
                  inputClassName={styles.OrganizationNameInput}
                  name={organizationNameFieldName}
                  resize="none"
                  clearable={isOrganizationNameFieldEnabled && isAutocomplete}
                  disabled={!isOrganizationNameFieldEnabled}
                  onClear={handleAutoCompleteClear}
                  inspectionResult={IROrganizationName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                />
                {!smevRequestStatus &&
                  isOrganizationNameFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {(isIFIZ || subjectType.label === KnmSubjectTypesEnum.FIZ) && (
            <Row>
              <FieldBlock
                id={organizationNameBlockId}
                label={getTitleFieldWithRedStar({
                  label: `Фамилия, имя, отчество (при наличии)`,
                  field: organizationNameFieldName,
                  isKnm: true,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  name={organizationNameFieldName}
                  disabled={!isFieldEnabled}
                  id={organizationNameBlockId}
                  parse={normalizeFio}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                id={addressBlockId}
                label="Адрес места нахождения"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={locationAddressFieldName} disabled={true} inspectionResult={IRLocationAddress} />
                {!smevRequestStatus &&
                  isFnsAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.UR && (
            <Row>
              <FieldBlock
                label="Место нахождения (осуществления деятельности) контролируемого лица"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={workAddressFieldName} disabled={true} />
                {!smevRequestStatus &&
                  isWorkAddressFnsError(subjectResult) &&
                  renderFnsErvkEqualsError(subjectResult, lookupData, isAutocomplete, styles.FnsMessage)}
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IP && (
            <Row>
              <FieldBlock
                label="Место фактического осуществления деятельности"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={factualAddressFieldName} disabled={true} inspectionResult={IRFactualAddress} />
              </FieldBlock>
            </Row>
          )}
          {isIFIZ && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Сведения о документе, удостоверяющем личность`,
                  field: documentFieldName,
                  isKnm: true,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={`acts[${listId}].document`}
              >
                <TextInputField name={documentFieldName} parse={parsePassport} disabled={!isFieldEnabled} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Наименование`,
                  field: organizationNameFieldName,
                  isKnm: true,
                })}
                id={organizationNameBlockId}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  id={organizationNameBlockId}
                  name={organizationNameFieldName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                  disabled={!isFieldEnabled}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.IUR || isIFIZ) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label:
                    subjectType.label === KnmSubjectTypesEnum.IUR
                      ? 'Государство, в котором указанное юридическое лицо зарегистрировано'
                      : 'Гражданство (при наличии)',
                  field: countryFieldName,
                  isKnm: true,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <SelectInputFieldAutoOptions
                  id={countryFieldName}
                  name={countryFieldName}
                  isClearable={false}
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.COUNTRIES}
                  disabled={!isFieldEnabled}
                  transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                />
              </FieldBlock>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  isChangePlanReasonActive: getKnmIsChangePlanReasonActive(state) || false,
  isEditableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isNewKnm: getKnmIsNew(state) || false,
  isSupervisor: getUserRoleIsSupervisor(state),
  knmDataApprovedType: getKnmDataErknmApproveApproved(state),
  knmDataNote: getKnmDataNote(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
});

export const ErknmSubjectInfo = connect(mapStateToProps)(ErknmSubjectInfoWithoutStore);
